import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { Store } from 'pinia'
import { ResourceItemModel } from './icon.model'
import debounce from 'lodash.debounce'
import { useRoute, useRouter } from 'vue-router'
import { bd09toGcj02, gcj02ToBd09 } from '@/utils/coordinateTransform'
import { showToast } from '@nio-fe/horo-h5'
import { MapViewMode, Marker, Point, ZoomMode } from '../../contract/map'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { getPowerAroundV2 } from '../../apis/map.api'
import { addMapResources, changeMarker, flyTo, getDistanceByViewport } from '../chargerMap/mapUtils'
import {
  DEFAULT_COUNTRY_ZOOM_LEVEL,
  DEFAULT_DISTRICT_ZOOM_LEVEL,
  SHOW_THIRD_ZOOM_LEVEL,
} from '../../config/zoomLevelConfig'
import { ResourcesModel } from '../../models/recommend.model'
import { PowerResourceType } from '@/contract/map.base'

/**
 * 城市icon渲染功能模块
 */
export type IconRenderProps = {
  onClickedMarker: (marker: Marker) => void
  cancelClickedMarker: () => void
  getResourcesInMap: () => ResourceItemModel[]
}
declare global {
  interface Window {
    getPowerAroundTimestamp: number
  }
}
export default defineComponent({
  name: 'icon-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
    clickedMarker: {
      type: Object as PropType<Marker>,
      default: null,
    },
  },

  setup: (props, { expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const zoomLevel = ref<number>(0)
    const zoomOld = ref<number>(0)
    const zoomNew = ref<number>(0)
    const route = useRoute()
    const router = useRouter()

    const clickedMarker = ref<Marker | null>() // 选中的点
    const clickedMarkerMap: {
      [id: string]: Marker
    } = {} // 缓存点击过的点，后面可以直接通过ID获取
    const fetchResourceFlag = ref<number>(1) // around资源点请求标识 1:未请求/开始请求 2:已请求完成

    let changeZoomOrCenter = false // zoomend dragend后会请求周围资源点，用于判断是否需要请求，用于路线和资源点的切换，切换后不需要请求
    let getPowerAroundTimestamp: number // 用于比对around接口请求到的数据是否是最新的
    let cacheClickedId = '' // 记录已经点击变大/已点击 的点
    let urlQuery: any = null // 记录url参数

    // 记录当前选中的markerId, 不受筛选影响
    const filterChangedSaveMarkerId = computed(() => clickedMarker.value?.id)

    let fromDetailToIcon = false // 从详情页跳转到另外一个icon，不需要清除backList，而是push

    /**
     * 请求around接口获取资源
     * 地图删除ICON数据 = 已渲染在地图上的ICON数据  - （接口请求数据  与  已渲染在地图上的ICON数据）
     * 地图新画增量ICON数据  = （接口请求数据 或 已渲染在地图上的ICON数据 ）- 已渲染在地图上的ICON数据
     */
    let resources: ResourceItemModel[] = [] // 地图上画点的资源点
    let resResources: ResourceItemModel[] = [] // 通过接口请求到的资源点
    const fetchMarkers = debounce(
      () => {
        const distance = getDistanceByViewport(mapContextRef.value!) // 获取视野范围的距离
        const requestTime = new Date().getTime()
        getPowerAroundTimestamp = requestTime
        const pointGcj = bd09toGcj02(mapContextRef.value?.getCenter() as Point)
        // TODO 缓存的话就保留小数点后8位
        // pointGcj.lng = Number(pointGcj.lng.toFixed(8))
        // pointGcj.lat = Number(pointGcj.lat.toFixed(8))
        zoomLevel.value = mapInstance.value?.getZoom()
        getPowerAroundV2(
          zoomLevel.value,
          props.componentStore.getFetchFilterParams(),
          pointGcj,
          distance
        ).then((res) => {
          if (requestTime == getPowerAroundTimestamp) {
            resResources = res.data.powers || []
            const resourcesIds = resources.map((item) => item.id) // 页面中已经存在的markers id
            const resTempMarkers = resResources.concat(
              resources.filter((a) => a.id === filterChangedSaveMarkerId.value)
            ) // 本次请求的markers + 保留点, 不需要额外判断filterChangedSaveMarkerId是否在resources中，后面的uniqueTempMarkers会去重
            const resTempMarkersIds = resTempMarkers.map((item) => item.id) // 本次请求的markers id
            const uniqueTempMarkers = resources
              .concat(resTempMarkers)
              .reduce((pre: ResourceItemModel[], cur: ResourceItemModel) => {
                if (!pre.find((item: ResourceItemModel) => item.id === cur.id)) {
                  pre.push(cur)
                }
                return pre
              }, []) // 去重后的markers
            const deleteMarkers = uniqueTempMarkers.filter((v) => !resTempMarkersIds.includes(v.id)) // 需要删除的markers
            const deleteMarkersIds = deleteMarkers.map((item) => item.id)
            const newMarkers = uniqueTempMarkers.filter((v) => !resourcesIds.includes(v.id)) // 需要新增的markers
            deleteMarkersIds.length && mapContextRef.value?.removeMarkers(deleteMarkersIds)
            if (
              zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL &&
              props.componentStore.viewMode == MapViewMode.Init
            ) {
              addMapResources(mapContextRef.value!, ZoomMode.City, newMarkers)
              resources = resTempMarkers
              fetchResourceFlag.value = 2
            } else {
              mapContextRef.value?.removeMarkers()
              resources = []
            }
            changeZoomOrCenter = false
          } else {
            console.log('updateCenterLocation 不是最新的请求，不需要更新')
          }
        })
      },
      500,
      {}
    )

    const getResourcesInMap = () => resources

    props.componentStore.bus.on('getUrlParamsIconRender', () => {
      fromDetailToIcon = true
      getUrlParams()
    })

    /**
     * 用于直接进入页面时，获取url参数，然后跳转到对应的点
     * @param backHome 是否后退重置为首页
     */
    const getUrlParams = (backHome = true) => {
      const id = (route.query.id as string) || ''
      if (route.name == 'resource' && id) {
        const location = (route.query.location as string) || ''
        const [lng, lat] = location?.split(',') || [0, 0]
        const point =
          lng && lat
            ? gcj02ToBd09({
                lng: Number(lng),
                lat: Number(lat),
              })
            : null
        const markerById = mapContextRef.value?.getMarkerById(id)
        if (markerById && mapContextRef.value?.getMap()?.getBounds()?.containsPoint(point)) {
          // 如果页面视野中存在这个点，不需要处理
          // watch的route.query.id会将icon变大
          point && flyTo(mapContextRef.value!, point, zoomLevel.value) // 移动到视野中间
          // 若是页面上已经存在这个点，先回触route.query.id的变化，触发changeMarkerSizeByRoute，导致cacheClickedId == customMarkerData.id
          // 所以需要设置checkSameId = false
          onClickedMarker(markerById.customData.marker, false)
        } else {
          mapContextRef.value?.removeMarkers([id])
          // 需要重置点击点, route.query.id改变会比这个快，导致icon被移除后没有重新加载，因为clickedMarker存在
          clickedMarker.value = null // 重置点击点
          props.componentStore.unsetDestLocationByPin()
          // 如果页面中不存在这个点，需要先请求周围资源点
          // const location = (route.query.location as string) || ''
          const pointType = (route.query.pointType as string) || ''
          const pointSubType = (route.query.pointSubType as string) || ''
          if (backHome) {
            // 用于后退重置为首页
            urlQuery = {
              id,
              location,
              pointType,
              pointSubType,
            }
            window.backHistoryNum = 0
            router.replace({
              name: 'home',
              state: {
                backHistoryNum: window.backHistoryNum,
              },
            }) // 为了保证回退时回到首页
            // router.replace('/') // 为了保证回退时回到首页
          }
          // const [lng, lat] = location?.split(',') || [0, 0]
          if (point) {
            // const point = gcj02ToBd09({
            //   lng: Number(lng),
            //   lat: Number(lat),
            // })
            // 将fetchResourceFlag状态置为1，用于fly后请求周围资源点完成监听状态变化
            fetchResourceFlag.value = 1
            flyTo(mapContextRef.value!, point, 17) // 会触发watch zoomLevel 变化请求around接口
            if (zoomLevel.value == 17) {
              // 有一个fly的过程，所以延迟2秒再请求周围资源点
              setTimeout(() => {
                // 如果当前层级已经是17，不会触发watch zoomLevel 变化请求around接口, 需要手动调用
                polyMarker()
              }, 2000)
            } else {
              // 如果当前层级不是17，会触发watch zoomLevel 变化请求around接口
            }
          }
        }
      }
    }

    /**
     * 回退触发点渲染
     */
    props.componentStore.bus.on('backResourceRoute', () => {
      zoomLevel.value = 0 // !!! 平托后再回退zoomLevel不变化，所以需要手动触发
      getUrlParams(false)
    })

    /**
     * 资源详情中点击点的跳转
     * @param item
     */
    const setNioStore = async (item: ResourcesModel) => {
      const temp = resources?.find((a) => a.id === item.id)
      props.componentStore.unsetDestLocationByPin()
      const routeTarget = {
        name: 'resource',
        query: {
          id: item.id,
          location: item.location,
          pointType: item.pointType,
          pointSubType: item.pointSubType,
        },
        state: {
          backHistoryNum: window.backHistoryNum,
        },
      }
      cacheClickedId = item.id // 从详情页跳转的，也需要改变记录的id
      if (temp) {
        // 地图上已经存在点
        await router.replace(routeTarget)
        props.componentStore.bus.emit('pushBackList', routeTarget)
        const [lng, lat] = item.location?.split(',') || [0, 0]
        const point =
          lng && lat
            ? gcj02ToBd09({
                lng: Number(lng),
                lat: Number(lat),
              })
            : null
        point && flyTo(mapContextRef.value!, point, zoomLevel.value) // 移动到视野中间
      } else {
        // 全不选不需要触发筛选重置
        if (!props.componentStore.filter.every((item) => !item.selected)) {
          // 20240523 修改 需要考虑资源不在视野中的情况
          props.componentStore.bus.emit('triggerFilter', {
            pointType: item.pointType,
            trigger: true,
          })
        }
        await router.replace(routeTarget)
        props.componentStore.bus.emit('getUrlParamsIconRender')
      }
    }
    props.componentStore.bus.on('setNioStore', setNioStore)

    /**
     * 画地图上的点
     * zoomend dragend后会请求周围资源点
     * 对于已点击的点 保持点击状态 即通过ID排除渲染
     */
    const polyMarker = async () => {
      fetchMarkers.cancel()
      if (props.componentStore.viewMode == MapViewMode.Init) {
        fetchMarkers()
      }
    }

    /**
     * 搜索项点击点
     */
    props.componentStore.bus.on('selectedMarker', () => {
      props.componentStore.$patch({
        cardLoaded: false,
      })
      // 异步，保证route变化后再执行
      getUrlParams()
    })

    /**
     * 筛选改变
     */
    props.componentStore.bus.on('filterChanged', polyMarker)

    // ------------暴露出去的方法--------------
    const zoomEndFnIcon = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
      zoomOld.value = zoomNew.value
      zoomNew.value = zoomLevel.value
      changeZoomOrCenter = true
    }
    const dragEndFnIcon = async () => {
      changeZoomOrCenter = true
      if (
        zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL &&
        props.componentStore.viewMode == MapViewMode.Init
      ) {
        // 显示大icon
        polyMarker()
      }
    }

    /**
     * 通过读取路由上的id，改变marker的大小
     * 用于路由跳转后，改变marker的大小
     */
    const changeMarkerSizeByRoute = () => {
      try {
        const id = (route.query.id as string) || ''
        changeMarker(mapContextRef.value!, id, clickedMarker.value?.id || '')
        clickedMarker.value =
          clickedMarkerMap[id] || mapContextRef.value?.getMarkerById(id).customData.marker || null
        cacheClickedId = id
        // 选中的点
        const resource: any = clickedMarker.value?.data
        const [lng, lat] = resource.location?.split(',') || [0, 0]
        const point = gcj02ToBd09({
          lng: Number(lng),
          lat: Number(lat),
        })
        // 设置目的地，显示小气泡
        props.componentStore.setDestLocationByPin({
          name: resource.name || 'Oops！未获取到名称',
          lng: point.lng,
          lat: point.lat,
          address: resource.address,
        })
      } catch (error) {
        console.log('=================大icon数据还未加载找不到点===================')
      }
    }

    /**
     * 通过id获取marker
     */
    props.componentStore.bus.on('onClickedMarkerByIdIconRender', (id: string) => {
      const marker = mapContextRef.value?.getMarkerById(id)
      if (marker) {
        onClickedMarker(mapContextRef.value?.getMarkerById(id).customData.marker)
      }
    })
    /**
     * 点击地图上的点
     * 不处理变大操作
     * @param customMarkerData 点的信息
     * @param checkSameId 是否检查是不是点击同一个点
     * @returns
     */
    const onClickedMarker = (customMarkerData: Marker, checkSameId = true) => {
      if (checkSameId && cacheClickedId == customMarkerData.id) {
        // 防止对同一个点多次点击
        return
      }
      cacheClickedId = customMarkerData.id
      if (props.componentStore.viewMode === MapViewMode.Init) {
        clickedMarkerMap[customMarkerData.id] = customMarkerData
        const resource: any = customMarkerData?.data
        props.componentStore.bus.emit('clickedMarker', customMarkerData) // 触发画圈
        if (resource) {
          const routeTarget = {
            name: 'resource',
            query: {
              id: resource.id,
              location: resource.location,
              pointType: resource.pointType,
              pointSubType: resource.pointSubType,
            },
            state: {
              backHistoryNum: window.backHistoryNum,
            },
          }
          // debugger
          if (route.name == 'home') {
            window.backHistoryNum++
            routeTarget.state.backHistoryNum = window.backHistoryNum
            router.push(routeTarget)
          } else if (route.name == 'data_summary') {
            // 从数据卡片直接点击资源点
            // if (window.client === 'mobile') {
            //   debugger
            //   window.backHistoryNum++
            //   routeTarget.state.backHistoryNum = window.backHistoryNum
            // }
            router.replace(routeTarget)
          } else {
            router.replace(routeTarget)
          }
          props.componentStore.bus.emit('iconOperateBackList', {
            routeTarget,
            fromDetailToIcon,
          })
          fromDetailToIcon = false
          props.componentStore.$patch({
            cardLoaded: true,
          })
          props.componentStore.bus.emit('preventZoomDragAction')
          // if (client === 'mobile') {
          //   // 移动端点击某个点 移动到视野中心
          //   const [lng, lat] = resource.location?.split(',') || [0, 0]
          //   const point =
          //     lng && lat
          //       ? gcj02ToBd09({
          //           lng: Number(lng),
          //           lat: Number(lat),
          //         })
          //       : null
          //   point && flyTo(mapContextRef.value!, point, zoomLevel.value) // 移动到视野中间
          // }
        }
      }
    }

    props.componentStore.bus.on('iconRenderClickedMarker', onClickedMarker)

    props.componentStore.bus.on('resetClickedMarker', () => {
      resetClickedMarker()
    })
    /**
     * 还原点击点的状态
     * 用于：路径规划缩放，路径规划tab切换， 筛选切换
     */
    const resetClickedMarker = () => {
      changeMarker(mapContextRef.value!, '', clickedMarker.value?.id || '') // 点变小
      cacheClickedId = '' // 重置点击点
      clickedMarker.value = null // 重置点击点
      props.componentStore.unsetDestLocationByPin() // 隐藏小气泡
    }

    props.componentStore.bus.on('showIconFromRecommend', () => {
      reShowIcon()
    })

    /**
     * 重新显示icon，重新请求或者读缓存
     */
    const reShowIcon = () => {
      if (props.componentStore.viewMode === MapViewMode.Init) {
        if (changeZoomOrCenter) {
          if (zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL) {
            // 显示大icon
            polyMarker()
          }
        } else {
          addMapResources(mapContextRef.value!, ZoomMode.City, resources)
        }
      }
    }

    /**
     * 取消icon点击，关闭icon详情
     */
    const cancelClickedMarker = () => {
      if (resResources.map((item) => item.id).includes(filterChangedSaveMarkerId.value!)) {
        // 变小
        changeMarker(mapContextRef.value!, '', clickedMarker.value?.id || '')
      } else {
        // 删除
        const index = resources.findIndex((item) => item.id === filterChangedSaveMarkerId.value)
        resources.splice(index, 1)
        mapContextRef.value?.removeMarkers([filterChangedSaveMarkerId.value!])
      }
      clickedMarker.value = null
      props.componentStore.unsetDestLocationByPin()
    }

    /**
     * 手动清空所有icon
     */
    props.componentStore.bus.on('clearAllMarkers', () => {
      mapContextRef.value?.removeMarkers()
    })

    const zoomChange = (newVal: number, oldVal: number) => {
      if (props.componentStore.viewMode == MapViewMode.Init && zoomLevel.value > 0) {
        if (zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL) {
          // 显示大icon
          polyMarker()
        } else {
          fetchMarkers.cancel()
          // 清除ICON
          mapContextRef.value?.removeMarkers()
          resources = []

          // 20240718修复问题
          if (zoomLevel.value > DEFAULT_COUNTRY_ZOOM_LEVEL) {
            // 路线层级下，显示小点，需要关闭详情卡片
            // zoom 到小点显示的情况下如果没有关闭详情卡片，手动关闭
            if (route.name == 'resource') {
              // clearHistory()
              props.componentStore.bus.emit('closeResourceCard')
            }
          } else {
            // 全国模式下会触发filter change（三方点），所以不需要手动触发
          }
        }
      } else {
        if (newVal < SHOW_THIRD_ZOOM_LEVEL && oldVal >= SHOW_THIRD_ZOOM_LEVEL) {
          // --------------------------------
          // 20240606修复问题
          // 1. 搜索花卷蔚来中心002  2. 选择pin点  3. 放大地图出现三方点 3. 地图上点击花卷蔚来中心002试驾点 4. 回退
          // 结果： 未清除三方点
          // --------------------------------
          // 若小于14级，不显示三方点
          const thirdMarkersIds: string[] = []
          const newResources: ResourceItemModel[] = []
          resources.forEach((item: ResourceItemModel) => {
            if (item.pointSubType !== PowerResourceType.ThirdChargeStation) {
              newResources.push(item)
            } else {
              thirdMarkersIds.push(item.id)
            }
          })
          if (thirdMarkersIds.length) {
            mapContextRef.value?.removeMarkers(thirdMarkersIds)
            resources = newResources
          }
        }
        if (clickedMarker.value) {
          // 仅保留选中的点
          resources = resources.filter((val) => val.id === clickedMarker.value?.id)
        } else {
          resources = []
        }
      }
    }

    props.componentStore.bus.on('zoomChangeIcon', () => {
      console.log('==================zoomChangeIcon==================')
      console.log(zoomNew.value)
      console.log(zoomOld.value)
      console.log('====================================')
      zoomChange(zoomNew.value, zoomOld.value)
    })

    expose({
      onClickedMarker,
      cancelClickedMarker,
      getResourcesInMap,
    })

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEndFnIcon, false)
      mapInstance.value?.addEventListener('dragend', dragEndFnIcon, false)
    }

    // unmounted时摧毁相关事件监听
    const destroyEventListener = () => {
      mapInstance.value?.removeEventListener('zoomend', zoomEndFnIcon, false)
      mapInstance.value?.removeEventListener('dragend', dragEndFnIcon, false)
    }

    onUnmounted(() => {
      destroyEventListener()
      props.componentStore.bus.off('selectedMarker')
      props.componentStore.bus.off('filterChanged', polyMarker)
      props.componentStore.bus.off('getUrlParams')
      props.componentStore.bus.off('backResourceRoute')
      props.componentStore.bus.off('resetClickedMarker')
      props.componentStore.bus.off('onClickedMarkerByIdIconRender')
      props.componentStore.bus.off('showIconFromRecommend')
      props.componentStore.bus.off('setNioStore')
      props.componentStore.bus.off('clearAllMarkers')
      mapContextRef.value?.removeMarkers()
      props.componentStore.bus.off('iconRenderClickedMarker')
      props.componentStore.bus.off('zoomChangeIcon')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      // mapInstance.value = mapContextRef.value?.getMap()
      initEventListener()
      getUrlParams() // 读取url参数，用于直接通过url进入页面
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // mapContextRef.value = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    // --------------------------
    watch(
      () => route.query.id,
      () => {
        changeMarkerSizeByRoute()
      },
      {
        immediate: true,
      }
    )
    watch(
      () => route.name,
      (newVal, oldVal) => {
        if (newVal !== 'resource') {
          cacheClickedId = ''
        }
        if (newVal !== 'resource' && newVal !== 'pin' && oldVal === 'resource') {
          // 从icon点击非icon
          cancelClickedMarker()
          // if (newVal == 'home') {
          //   // 资源点到pin不用清除
          //   props.componentStore.unsetDestLocationByPin()
          // }
        }
        if (
          (newVal === 'resource' && oldVal === 'plan') ||
          (newVal === 'pin' && oldVal === 'plan')
        ) {
          // 中途打断或者返回
          resources = [] // 清空上一次的资源点
          fetchResourceFlag.value = 1 // 重置状态为未请求
          cacheClickedId = ''
          changeZoomOrCenter = true
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => props.componentStore.viewMode,
      (newVal, oldVal) => {
        if (newVal !== MapViewMode.Init) {
          changeZoomOrCenter = false
          mapContextRef.value?.removeMarkers()
        } else {
          if (oldVal === MapViewMode.Recommend) {
            // 其他模式回到初始模式 且显示icon的情况下
            if (changeZoomOrCenter) {
              if (zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL) {
                resources = [] // 清空上一次的资源点
                // 显示大icon
                polyMarker()
              }
            } else {
              addMapResources(mapContextRef.value!, ZoomMode.City, resources)
            }
          } else if (oldVal === MapViewMode.WishPower) {
            resources = [] // 清空上一次的资源点
            // 心愿站切换回来
            polyMarker()
          }
        }
      }
    )

    const fetchResourceFlagChange = () => {
      if (fetchResourceFlag.value === 2 && route.query.id) {
        const marker = mapContextRef.value?.getMarkerById(route.query.id as string).customData
          .marker
        onClickedMarker(marker)
        changeMarkerSizeByRoute()
        return
      }
      if (fetchResourceFlag.value === 2 && urlQuery?.id) {
        const marker = mapContextRef.value?.getMarkerById(urlQuery.id).customData.marker
        onClickedMarker(marker)
        urlQuery = null // 用完清空, 防止重复触发
        return
      }
    }
    watch(
      () => fetchResourceFlag.value,
      () => {
        try {
          fetchResourceFlagChange()
        } catch (error) {
          polyMarker()
          showToast({
            content: '暂不可见',
            type: 'failure',
          })
        }
      }
    )
    watch(() => zoomLevel.value, zoomChange)
    return () => {}
  },
})
